import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMatch } from 'react-router-dom'
import { useCartaDigitalPublica } from '@hooks/publicaciones-cartas-digitales/useCartaDigitalPublica'
import type {
    CartaDigitalPublicaState,
    CartaDigitalPublicaStatus,
} from '@hooks/publicaciones-cartas-digitales/useCartaDigitalPublica'
import type { CartaDigitalPublica } from '@models/carta-digital-publica'
import { constants } from '@utils/constants'
import type { MenuTemaModo } from './menuTheme'
import {
    DEFAULT_MENU_COLOR_PRINCIPAL,
    DEFAULT_MENU_TEMA_MODO,
    normalizeMenuColorPrincipal,
    normalizeMenuTemaModo,
} from './menuTheme'

export type MenuBrandingState = {
    isFound: boolean
    logoUrl: string | null
    restaurantName: string | null
    branchName: string | null
    pagosOnlineActivos: boolean
    temaModo: MenuTemaModo
    colorPrincipal: string
}

const initialBranding: MenuBrandingState = {
    isFound: false,
    logoUrl: null,
    restaurantName: null,
    branchName: null,
    pagosOnlineActivos: false,
    temaModo: DEFAULT_MENU_TEMA_MODO,
    colorPrincipal: DEFAULT_MENU_COLOR_PRINCIPAL,
}

type MenuBrandingValue = MenuBrandingState & {
    cartaStatus: CartaDigitalPublicaStatus
    cartaData: CartaDigitalPublica | null
}

const MenuBrandingContext = createContext<MenuBrandingValue | null>(null)
const MENU_BRANDING_STORAGE_PREFIX = 'menu_branding:'

type CachedMenuBranding = Pick<
    MenuBrandingState,
    'temaModo' | 'colorPrincipal' | 'logoUrl' | 'restaurantName' | 'branchName'
>

const getStorageKey = (slug: string) => `${MENU_BRANDING_STORAGE_PREFIX}${slug}`

const readCachedBranding = (slug?: string): CachedMenuBranding | null => {
    if (!slug) return null

    try {
        const value =
            localStorage.getItem(getStorageKey(slug)) ??
            localStorage.getItem(`menu_theme:${slug}`)
        if (!value) return null

        const parsed = JSON.parse(value) as Partial<CachedMenuBranding>
        return {
            temaModo: normalizeMenuTemaModo(parsed.temaModo),
            colorPrincipal: normalizeMenuColorPrincipal(parsed.colorPrincipal),
            logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
            restaurantName:
                typeof parsed.restaurantName === 'string' ? parsed.restaurantName : null,
            branchName: typeof parsed.branchName === 'string' ? parsed.branchName : null,
        }
    } catch {
        return null
    }
}

const writeCachedBranding = (slug: string, branding: CachedMenuBranding) => {
    try {
        localStorage.setItem(getStorageKey(slug), JSON.stringify(branding))
    } catch {
        // El branding puede seguir funcionando en memoria si el almacenamiento no está disponible.
    }
}

const removeCachedBranding = (slug?: string) => {
    if (!slug) return

    try {
        localStorage.removeItem(getStorageKey(slug))
        // Clave anterior del cache de tema (migración suave).
        localStorage.removeItem(`menu_theme:${slug}`)
    } catch {
        // No se requiere acción adicional.
    }
}

const brandingFromCache = (slug?: string): MenuBrandingState => ({
    ...initialBranding,
    ...(readCachedBranding(slug) ?? {}),
})

const resolveLogoUrl = (carta: CartaDigitalPublica): string | null => {
    const path = carta.carta_digital.path_logo ?? carta.sucursal.sc_path_logo
    return path ? `${constants.api.base}/${path}` : null
}

type MenuBrandingProviderProps = {
    children: ReactNode
}

export function MenuBrandingProvider({ children }: MenuBrandingProviderProps) {
    const menuMatch = useMatch({ path: '/menu/:slug', end: false })
    const slug = menuMatch?.params.slug
    const cartaState = useCartaDigitalPublica(slug)
    const [branding, setBranding] = useState<MenuBrandingState>(() => brandingFromCache(slug))

    const safeCartaState = useMemo<CartaDigitalPublicaState>(
        () =>
            cartaState.data && cartaState.data.slug !== slug
                ? { status: 'loading', data: null }
                : cartaState,
        [cartaState, slug]
    )

    useEffect(() => {
        setBranding(brandingFromCache(slug))
    }, [slug])

    useEffect(() => {
        const { status, data } = safeCartaState

        if ((status === 'found' || status === 'disabled') && data && slug) {
            const temaModo = normalizeMenuTemaModo(data.carta_digital.tema_modo)
            const colorPrincipal = normalizeMenuColorPrincipal(
                data.carta_digital.color_principal
            )
            const branchName =
                data.sucursal.sc_nombre_comercial_sucursal ??
                data.sucursal.sc_descripcion ??
                null

            const logoUrl = resolveLogoUrl(data)
            const restaurantName =
                data.carta_digital.mostrar_nombre_restaurante !== false
                    ? data.sucursal.sc_restaurante ?? null
                    : null

            setBranding({
                isFound: status === 'found',
                logoUrl,
                restaurantName,
                branchName,
                pagosOnlineActivos: Boolean(data.pagos_online_activos),
                temaModo,
                colorPrincipal,
            })
            writeCachedBranding(slug, {
                temaModo,
                colorPrincipal,
                logoUrl,
                restaurantName,
                branchName,
            })
            return
        }

        if (status === 'not_found') {
            removeCachedBranding(slug)
            setBranding(initialBranding)
        }
    }, [safeCartaState, slug])

    const value = useMemo(
        () => ({
            ...branding,
            cartaStatus: safeCartaState.status,
            cartaData: safeCartaState.data,
        }),
        [branding, safeCartaState]
    )

    return <MenuBrandingContext.Provider value={value}>{children}</MenuBrandingContext.Provider>
}

export const useMenuBranding = () => {
    const context = useContext(MenuBrandingContext)
    if (!context) {
        throw new Error('useMenuBranding debe usarse dentro de MenuBrandingProvider')
    }
    return context
}
